import type { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { JyotishaEthicsModal as JyotishaEthicsModalJs } from 'schools/parashari/components/jyotisha/JyotishaEthicsModal';
import { useAuth } from 'schools/parashari/contexts/AuthContext';
import { usePlatformTheme } from 'schools/parashari/hooks/usePlatformTheme';
import { useAppDispatch } from 'hooks/useAppStore';
import { logout } from 'features/auth/authSlice';
import { signOut, isSupabaseConfigured } from 'features/auth/api/authService';
import { ROUTES } from 'constants/routes';

type EthicsUser = {
  email?: string | null;
  user_metadata?: { display_name?: string };
} | null;

type EthicsModalProps = {
  isDark: boolean;
  open: boolean;
  required?: boolean;
  user?: EthicsUser;
  hasConsent?: boolean;
  busy?: boolean;
  error?: string;
  onClose?: () => void;
  onAcceptConsent?: () => void | Promise<void>;
  onSignOut?: () => void | Promise<void>;
};

/** Parashari JS modal - typed here because `schools/parashari` is excluded from tsc. */
const JyotishaEthicsModal = JyotishaEthicsModalJs as ComponentType<EthicsModalProps>;

type LearnerEthicsModalProps = {
  open: boolean;
  onClose: () => void;
  required?: boolean;
  hasConsent: boolean;
  consentBusy: boolean;
  consentError: string;
  onAcceptConsent: () => Promise<void>;
};

export function LearnerEthicsModal({
  open,
  onClose,
  required = false,
  hasConsent,
  consentBusy,
  consentError,
  onAcceptConsent,
}: LearnerEthicsModalProps) {
  const { user } = useAuth() as { user: EthicsUser };
  const { isDark } = usePlatformTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleAcceptConsent = async () => {
    try {
      await onAcceptConsent();
      onClose();
    } catch {
      // consentError is set in provider
    }
  };

  const handleSignOut = () => {
    dispatch(logout());
    navigate(ROUTES.HOME);
    if (isSupabaseConfigured) {
      void signOut();
    }
  };

  return (
    <JyotishaEthicsModal
      isDark={isDark}
      open={open}
      required={required}
      user={user}
      hasConsent={hasConsent}
      busy={consentBusy}
      error={consentError}
      onClose={required ? undefined : onClose}
      onAcceptConsent={handleAcceptConsent}
      onSignOut={handleSignOut}
    />
  );
}
